import React, { useEffect, useMemo, useRef, useState } from "react";
import notifications from "../../common/Components/Notifications/Notification";
import { listKarkhanaWorkerProfiles, uploadWorkerPhoto } from "../../services/karkhanaApi";
import "../gi-registry/gi-registry.scss";
import { useKT } from "./i18n/kt";
import { shrinkPhoto } from "./people/photo";
import PageHeader from "./ui/PageHeader";
import "./home/karkhana-home.scss";
import "./people/quick-add.scss";
import "./shell/karkhana-more.scss";
import { kp } from "./karkhanaPaths";

/**
 * A quick round on the floor after an import: one face photo per person, the next name comes up
 * by itself. Skip anyone who isn't there; they stay on the list for next time.
 */
const KarkhanaWorkerPhotosPage = () => {
  const { t } = useKT();
  const [people, setPeople] = useState<any[] | null>(null);
  const [skipped, setSkipped] = useState<string[]>([]);
  const [done, setDone] = useState(0);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    (async () => {
      const all: any[] = [];
      for (let page = 0; page < 20; page++) {
        const res: any = await listKarkhanaWorkerProfiles(page, 200).catch(() => null);
        const docs = res?.success ? res.data?.docs || [] : [];
        all.push(...docs);
        if (docs.length < 200) break;
      }
      setPeople(all.filter((w) => w.active !== false && !w.photoUrl));
    })();
  }, []);

  const queue = useMemo(() => (people || []).filter((p) => !skipped.includes(p.id)), [people, skipped]);
  const current = queue[0];
  const total = (people?.length || 0) + done;

  const take = async (file?: File | null) => {
    if (!file || !current) return;
    setBusy(true);
    try {
      const small = await shrinkPhoto(file);
      await uploadWorkerPhoto(current.id, small.blob, small.type || "image/jpeg");
      setPeople((list) => (list || []).filter((p) => p.id !== current.id));
      setDone((n) => n + 1);
    } catch (e: any) {
      notifications({ title: t("Photo not saved"), message: t(e?.message || "The photo didn't upload. Try again"), type: "danger" });
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="gi-registry-page kw-page kh-home">
      <PageHeader title={t("Take photos")} back={kp.workers()} />
      {people === null ? (
        <div className="kw-empty">{t("Loading…")}</div>
      ) : !current ? (
        <div className="kh-start">
          <h3>{done || total ? t("Everyone has a photo") : t("No one to photograph")}</h3>
          <p>{skipped.length ? t("{{n}} skipped. They stay on the list for next time.", { n: skipped.length }) : t("New people get their photo when they are added.")}</p>
        </div>
      ) : (
        <>
          <div className="kpr-progress">
            <span>{t("{{done}} of {{n}} done", { done, n: total })}</span>
            <div className="bar">
              <i style={{ width: `${total ? Math.round((done / total) * 100) : 0}%` }} />
            </div>
          </div>
          <div className="kpr-card">
            <button type="button" className="kpr-cam" disabled={busy} onClick={() => fileRef.current?.click()} aria-label={t("Take photo")}>
              <span className="kpr-frame" aria-hidden="true" />
              <span>{busy ? t("Saving…") : t("Tap to open the camera")}</span>
            </button>
            <strong className="kpr-name">{current.name}</strong>
            <span className="kpr-code">
              <span className="kw-code">{current.workerCode}</span>
            </span>
            <input ref={fileRef} type="file" accept="image/*" capture="user" hidden onChange={(e) => take(e.target.files?.[0])} />
            <div className="kq__foot">
              <button type="button" className="btn btn-outline-secondary" disabled={busy} onClick={() => setSkipped((s) => [...s, current.id])}>
                {t("Skip")}
              </button>
              <button type="button" className="btn ResetBtn" disabled={busy} onClick={() => fileRef.current?.click()}>
                📷 {t("Take photo")}
              </button>
            </div>
          </div>
          {queue.length > 1 ? (
            <>
              <h3 className="kpr-next">{t("Next")}</h3>
              <div className="km-list">
                {queue.slice(1, 6).map((p) => (
                  <div key={p.id} className="kpr-row">
                    <span className="kw-code">{p.workerCode}</span> {p.name}
                  </div>
                ))}
              </div>
            </>
          ) : null}
        </>
      )}
    </div>
  );
};

export default KarkhanaWorkerPhotosPage;
